import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import moment from "moment";
import { HTTP_METHODS } from "../../common/constants";
import HTTPClient from "../../common/servicers/httpClient";

/**************************************
 *********** API ACTIONS ***************
 ***************************************/

export const getInvitations = createAsyncThunk(
  "getInvitations",
  async (
    { useCache = true, organizationId },
    { getState, requestId, rejectWithValue },
  ) => {
    const { currentRequestId, loadingInvites, invitations } =
      getState().dashboard.admin;
    if (!loadingInvites || requestId !== currentRequestId) {
      return;
    }
    if (useCache && invitations && invitations[organizationId]) {
      return { data: invitations[organizationId], organizationId };
    }
    try {
      const response = await new HTTPClient({
        endpoint: `/admins/organizations/${organizationId}/invitations`,
        method: HTTP_METHODS.GET,
      }).callAuthorizedAPI();
      return { ...response.data, organizationId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const sendInvites = createAsyncThunk(
  "sendInvite",
  async (
    { payload, organizationId },
    { getState, requestId, rejectWithValue },
  ) => {
    const { sendInvitesRequestId, sendingInvite } = getState().dashboard.admin;
    if (!sendingInvite || sendInvitesRequestId !== requestId) {
      return;
    }
    try {
      const response = await new HTTPClient({
        endpoint: `admins/organizations/${organizationId}/invitations`,
        method: HTTP_METHODS.POST,
        data: payload,
      }).callAuthorizedAPI();
      return { ...response.data, organizationId };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const updateIdentityRole = createAsyncThunk(
  "updateIdentityRole",
  async (
    { payload, organizationId, identityId },
    { getState, requestId, rejectWithValue },
  ) => {
    const { updateIdentityRoleRequestId, updatingIdentityRole } =
      getState().dashboard.admin;
    if (!updatingIdentityRole || updateIdentityRoleRequestId !== requestId) {
      return;
    }
    try {
      const response = await new HTTPClient({
        endpoint: `admins/organizations/${organizationId}/identities/${identityId}/roles`,
        method: HTTP_METHODS.PUT,
        data: payload,
      }).callAuthorizedAPI();
      return { ...response.data, organizationId, identityId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const resendInvite = createAsyncThunk(
  "resendInvite",
  async (
    { organizationId, invitationId },
    { getState, requestId, rejectWithValue },
  ) => {
    const { currentRequestId, resendingInvite } = getState().dashboard.admin;
    if (!resendingInvite || currentRequestId !== requestId) {
      return;
    }
    try {
      const response = await new HTTPClient({
        endpoint: `admins/organizations/${organizationId}/invitations/${invitationId}`,
        method: HTTP_METHODS.PUT,
      }).callAuthorizedAPI();
      return { ...response.data, organizationId };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const updateIdentityPermissions = createAsyncThunk(
  "updateIdentityPermissions",
  async (
    { organizationId, tenantId, identityId, tenanatPermissions, tenantDetails },
    { getState, requestId, rejectWithValue },
  ) => {
    const { updatePermissionsRequestId, updatingPermissions } =
      getState().dashboard.admin;
    if (!updatingPermissions || updatePermissionsRequestId !== requestId) {
      return;
    }
    try {
      const response = await new HTTPClient({
        endpoint: `/organizations/${organizationId}/tenants/${tenantId}/identities/${identityId}/permissions`,
        method: HTTP_METHODS.PUT,
        data: tenanatPermissions,
      }).callAuthorizedAPI();
      return {
        ...response.data,
        selectedTenantId: tenantId,
        identityId,
        organizationId,
        tenantDetails,
      };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteInvite = createAsyncThunk(
  "deleteInvite",
  async (
    { invitations, organizationId },
    { getState, requestId, rejectWithValue },
  ) => {
    const { deleteInviteRequestId, deletingInvite } =
      getState().dashboard.admin;
    if (!deletingInvite || deleteInviteRequestId !== requestId) {
      return;
    }
    try {
      await new HTTPClient({
        endpoint: `admins/organizations/${organizationId}/invitations`,
        method: HTTP_METHODS.DELETE,
        data: invitations,
      }).callAuthorizedAPI();
      return { organizationId, invitations };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteIdentity = createAsyncThunk(
  "deleteIdentity",
  async (
    { identities, organizationId },
    { getState, requestId, rejectWithValue },
  ) => {
    const { deleteIdentityRequestId, deletingIdentity } =
      getState().dashboard.admin;
    if (!deletingIdentity || deleteIdentityRequestId !== requestId) {
      return;
    }
    try {
      await new HTTPClient({
        endpoint: `admins/organizations/${organizationId}/identities`,
        method: HTTP_METHODS.DELETE,
        data: identities,
      }).callAuthorizedAPI();
      return { organizationId, identities };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getIdentity = createAsyncThunk(
  "getIdentity",
  async (
    { useCache = true, organizationId, identityId },
    { getState, requestId, rejectWithValue },
  ) => {
    const { currentRequestId, loading, identity } = getState().dashboard.admin;
    if (!loading || requestId !== currentRequestId) {
      return;
    }
    if (useCache && identity && identity[identityId]) {
      return { data: identity[identityId], identityId };
    }
    try {
      const response = await new HTTPClient({
        endpoint: `/identities/identity/${organizationId}/${identityId}`,
        method: HTTP_METHODS.GET,
      }).callAuthorizedAPI();
      return { ...response.data, identityId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getIdentities = createAsyncThunk(
  "getIdentities",
  async (
    { useCache = true, organizationId },
    { getState, requestId, rejectWithValue },
  ) => {
    const { currentRequestId, userLoading, identities } =
      getState().dashboard.admin;
    if (!userLoading || requestId !== currentRequestId) {
      return;
    }
    if (useCache && identities && identities[organizationId]) {
      return { data: identities[organizationId], organizationId };
    }
    try {
      const response = await new HTTPClient({
        endpoint: `/identities/${organizationId}`,
        method: HTTP_METHODS.GET,
      }).callAuthorizedAPI();
      return { ...response.data, organizationId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const initialState = {
  invitations: {},
  identity: {},
  identities: {},
  loading: false,
  userLoading: false,
  error: null,
  currentRequestId: null,
  loaderText: null,
  sendingInvite: null,
  sendInvitesRequestId: null,
  sendInvitesError: null,
  deletingInvite: null,
  deleteInviteError: null,
  deleteInviteRequestId: null,
  deletingIdentity: null,
  deleteIdentityError: null,
  deleteIdentityRequestId: null,
  resendingInvite: null,
  resendingInviteError: null,
  updatePermissionsRequestId: null,
  updatingPermissions: null,
  errorUpdatingPermissions: null,
  updatingIdentityRole: false,
  updateIdentityRoleError: null,
  updateIdentityRoleRequestId: null,
};

export const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    resetErrors: (state) => {
      state.deleteInviteError = null;
      state.deleteIdentityError = null;
      state.sendInvitesError = null;
      state.resendingInviteError = null;
      state.updateIdentityRoleError = null;
    },
  },
  extraReducers: {
    [getInvitations.pending]: (state, action) => {
      state.loadingInvites = true;
      state.currentRequestId = action.meta.requestId;
      state.deleteInviteError = null;
    },
    [getInvitations.fulfilled]: (state, action) => {
      state.currentRequestId = null;
      state.loadingInvites = false;
      const now = moment(new Date());
      state.invitations[action.payload.organizationId] = action.payload?.data
        ? action.payload.data.map((d) => {
            let temp = { ...d };
            temp.name = d.identityInvitationId;
            temp.expired =
              now.diff(moment(d?.creationDate), "days") > 15 ? true : false;
            return temp;
          })
        : null;
    },
    [getInvitations.rejected]: (state, action) => {
      state.currentRequestId = null;
      state.error = action.payload || true;
      state.loadingInvites = false;
    },
    [getIdentities.pending]: (state, action) => {
      state.userLoading = true;
      state.currentRequestId = action.meta.requestId;
      state.deleteIdentityError = null;
    },
    [getIdentities.fulfilled]: (state, action) => {
      state.currentRequestId = null;
      state.userLoading = false;
      state.identities[action.payload.organizationId] = action.payload?.data
        ? action.payload.data.map((d) => {
            return { ...d, email: d.username, active: true };
          })
        : null;
    },
    [getIdentities.rejected]: (state, action) => {
      state.currentRequestId = null;
      state.error = action.payload || true;
      state.userLoading = false;
    },
    [updateIdentityPermissions.pending]: (state, action) => {
      state.updatingPermissions = true;
      state.updatePermissionsRequestId = action.meta.requestId;
      state.errorUpdatingPermissions = null;
    },
    [updateIdentityPermissions.fulfilled]: (state, action) => {
      state.updatePermissionsRequestId = null;
      state.updatingPermissions = false;
      const identity = state.identity[action.payload.identityId];
      // check if tenantPermissions for that tenant exists or not
      const tenantPermissionsForSelectedTenant =
        identity.tenantPermissions.find(
          (t) => t.tenant.tenantId === action.payload.selectedTenantId,
        );
      if (tenantPermissionsForSelectedTenant) {
        const tenantPermissions = identity.tenantPermissions.map(
          (tenantPermission) => {
            if (
              tenantPermission.tenant.tenantId ===
              action.payload.selectedTenantId
            ) {
              return { ...tenantPermission, permissions: action.payload.data };
            } else {
              return tenantPermission;
            }
          },
        );
        state.identity[action.payload.identityId].tenantPermissions =
          tenantPermissions;
      } else {
        state.identity[action.payload.identityId].tenantPermissions.push({
          permissions: action.payload.data,
          tenant: action.payload.tenantDetails,
        });
      }
    },
    [updateIdentityPermissions.rejected]: (state, action) => {
      state.updatePermissionsRequestId = null;
      state.errorUpdatingPermissions = action.payload || true;
      state.updatingPermissions = false;
    },

    [sendInvites.pending]: (state, action) => {
      state.sendingInvite = true;
      state.sendInvitesRequestId = action.meta.requestId;
      state.sendInvitesError = null;
    },
    [sendInvites.fulfilled]: (state, action) => {
      state.sendingInvite = false;
      state.sendInvitesRequestId = null;
      state.sendInvitesError = "";
      state.invitations[action.payload.organizationId] = action.payload?.data
        ? action.payload.data
        : null;
    },
    [sendInvites.rejected]: (state, action) => {
      state.sendingInvite = false;
      state.sendInvitesRequestId = null;
      const errorCode = action.payload?.code;
      if (errorCode === 409) {
        state.sendInvitesError = "One of the email Ids is already invited";
      } else {
        const errorMessage = action.payload?.message || action.error?.message;
        state.sendInvitesError =
          typeof errorMessage === "string"
            ? errorMessage
            : "Error sending invite";
      }
    },
    [resendInvite.pending]: (state, action) => {
      state.resendingInvite = true;
      state.currentRequestId = action.meta.requestId;
      state.resendingInviteError = null;
    },
    [resendInvite.fulfilled]: (state) => {
      state.resendingInvite = false;
      state.currentRequestId = null;
      state.resendingInviteError = "";
    },
    [resendInvite.rejected]: (state, action) => {
      state.resendingInvite = false;
      state.currentRequestId = null;
      const errorMessage = action.payload?.message || action.error?.message;
      state.resendingInviteError =
        typeof errorMessage === "string"
          ? errorMessage
          : "Error resending invite";
    },
    [deleteInvite.pending]: (state, action) => {
      state.deletingInvite = true;
      state.deleteInviteError = null;
      state.deleteInviteRequestId = action.meta.requestId;
    },
    [deleteInvite.fulfilled]: (state, action) => {
      state.deleteInviteRequestId = null;
      state.deletingInvite = false;
      state.deleteInviteError = "";
      if (action.payload.organizationId) {
        state.invitations[action.payload.organizationId] = state.invitations[
          action.payload.organizationId
        ].filter(
          (i) =>
            action.payload.invitations.indexOf(i.identityInvitationId) === -1,
        );
      }
    },
    [deleteInvite.rejected]: (state, action) => {
      state.deletingInvite = false;
      state.deleteInviteError = action.payload?.message
        ? action.payload?.message
        : action.error
          ? action.error
          : true;
      state.deleteInviteRequestId = null;
    },
    [deleteIdentity.pending]: (state, action) => {
      state.deletingIdentity = true;
      state.deleteIdentityError = null;
      state.deleteIdentityRequestId = action.meta.requestId;
    },
    [deleteIdentity.fulfilled]: (state, action) => {
      state.deleteIdentityRequestId = null;
      state.deletingIdentity = false;
      state.deleteIdentityError = "";
      if (action.payload.organizationId) {
        state.identities[action.payload.organizationId] = state.identities[
          action.payload.organizationId
        ].filter((i) => action.payload.identities.indexOf(i.identityId) === -1);
      }
    },
    [deleteIdentity.rejected]: (state, action) => {
      state.deletingIdentity = false;
      state.deleteIdentityError = action.payload?.message
        ? action.payload?.message
        : action.error
          ? action.error
          : true;
      state.deleteIdentityRequestId = null;
    },
    [updateIdentityRole.pending]: (state, action) => {
      state.updatingIdentityRole = true;
      state.updateIdentityRoleError = null;
      state.updateIdentityRoleRequestId = action.meta.requestId;
    },
    [updateIdentityRole.fulfilled]: (state, action) => {
      state.updateIdentityRoleRequestId = null;
      state.updatingIdentityRole = false;
      state.updateIdentityRoleError = "";
      if (state.identity[action.payload.identityId]) {
        state.identity[action.payload.identityId] = action.payload.data;
      }
      if (
        action.payload.organizationId &&
        state.identities[action.payload.organizationId]
      ) {
        state.identities[action.payload.organizationId] = state.identities[
          action.payload.organizationId
        ].map((i) => {
          if (i.identityId === action.payload.identityId) {
            return action.payload.data;
          }
          return i;
        });
      }
    },
    [updateIdentityRole.rejected]: (state, action) => {
      state.updatingIdentityRole = false;
      state.updateIdentityRoleError = action.payload?.message
        ? action.payload?.message
        : action.error
          ? action.error
          : true;
      state.updateIdentityRoleRequestId = null;
    },
    [getIdentity.pending]: (state, action) => {
      state.loading = true;
      state.currentRequestId = action.meta.requestId;
    },
    [getIdentity.fulfilled]: (state, action) => {
      state.currentRequestId = null;
      state.loading = false;
      state.identity[action.payload.identityId] = action.payload?.data
        ? action.payload.data
        : null;
    },
    [getIdentity.rejected]: (state, action) => {
      state.currentRequestId = null;
      state.error = action.payload || true;
      state.loading = false;
    },
  },
});

export const { resetErrors } = adminSlice.actions;

export default adminSlice.reducer;
